import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from './lib/session';
import { routes } from './routes/routes';
import { NotConfigured } from './routes/NotConfigured';
import { RouteError } from './routes/RouteError';
import { announceNewVersion, startVersionWatch } from './lib/newVersion';
import { NewVersionBanner } from './shell/NewVersionBanner';
import { isConfigured, isSessionExpired, supabase } from './lib/supabase';
import './styles/tokens.css';
import '@fontsource-variable/archivo';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource/kalam/400.css';
import '@fontsource/kalam/700.css';
import '@fontsource/mukta/400.css';
import '@fontsource/mukta/600.css';
import './styles/themes.css';
import './styles/art.css';
import './styles/appearance.css';

/**
 * Session expiry. supabase-js refreshes the token in the background, so this is the rare case where it
 * could not — the refresh token was revoked, the idle timeout passed, the laptop slept through it. The
 * server has already refused; ending the local session makes the gate send the person to sign in, with
 * their place remembered, instead of leaving them on a screen where every panel says "JWT expired".
 */
let endingSession = false;
function onAuthFailure(error: unknown) {
  if (!isSessionExpired(error) || endingSession) return;
  endingSession = true;
  void supabase.auth.signOut({ scope: 'local' }).finally(() => {
    endingSession = false;
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthFailure }),
  mutationCache: new MutationCache({ onError: onAuthFailure }),
  defaultOptions: {
    queries: {
      // A refusal is a decision, not a hiccup: retrying a 403 just delays the honest message.
      retry: (failureCount, error) => {
        const code = (error as { code?: string }).code ?? '';
        if (code.startsWith('RTC_') && code !== 'RTC_INTERNAL') return false;
        // 30 Oct: one retry, five seconds later: a call that timed out on a busy database was retried twice at once and made the load worse.
        return failureCount < 1;
      },
      retryDelay: 5_000,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// A deploy renames the screens' files. A failed preload no longer reloads the page by itself (that could throw away a form being
// filled in): the person is told a new version is ready and refreshes when they choose. A screen that truly cannot open still
// reloads the desk once (RouteError), because the person is already moving to another screen.
window.addEventListener('vite:preloadError', (event) => {
  announceNewVersion();
  event.preventDefault();
});
startVersionWatch();

const router = createBrowserRouter(routes.map((route) => ({ ...route, errorElement: route.errorElement ?? <RouteError /> })));

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <NewVersionBanner />
    {isConfigured ? (
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </QueryClientProvider>
    ) : (
      // The app says plainly that it has no database rather than rendering a shell full of blanks that
      // looks like real, empty data.
      <NotConfigured />
    )}
  </StrictMode>,
);
